import React, { useEffect, useState } from 'react';
import { getSkuDualSourcedData } from 'utils/apiRequests';
import { roundToDecimalPlace } from './utils';
import { DualSourcedSkuProfileChart } from './DualSourcedSkuProfileChart';
import { colors } from '@alixpartners/ui-components';

export const DualSourcedSkuProfileChartContainer = ({ queryParams }) => {
	const [data, setData] = useState({
		singleSourcePrecentage: [],
		mutltisourcePercentage: [],
		categories: [],
		spend: [],
		singleSourceSpend: [],
		mutltisourceSpend: [],
	});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(
		() => {
			setData({
				singleSourcePrecentage: [],
				mutltisourcePercentage: [],
				categories: [],
				spend: [],
				singleSourceSpend: [],
				mutltisourceSpend: [],
			});
		},
		[queryParams, queryParams.dateFrom, queryParams.dateTo]
	);

	useEffect(
		() => {
			try {
				const params = {
					...queryParams,
					dimensions: [
						...Array(queryParams.hierarchy.length + 1).fill('clientCategory'),
						'sku',
					],
				};
				getSkuDualSourcedData(params).then(result => {
					const categories = [];
					const singleSourcePercentages = [];
					const multisourcePercentages = [];
					const spendValues = [];
					const singleSourceSpendValues = [];
					const mutltisourceSpendValues = [];

					result.body.forEach(ele => {
						categories.push(ele.name);
						singleSourcePercentages.push(ele.singleSourcePrecentage);
						multisourcePercentages.push(ele.mutltisourcePercentage);
						spendValues.push(ele.spend);
						singleSourceSpendValues.push(ele.singleSourceSpend);
						mutltisourceSpendValues.push(ele.mutltisourceSpend);
					});

					setData({
						categories: categories,
						singleSourcePrecentage: singleSourcePercentages,
						mutltisourcePercentage: multisourcePercentages,
						spend: spendValues,
						singleSourceSpend: singleSourceSpendValues,
						mutltisourceSpend: mutltisourceSpendValues,
					});
					setIsLoading(false);
				});
			} catch (error) {
				console.error('Failed to get data:', error);
				setIsLoading(false);
			}
		},
		[queryParams]
	);

	const formatSpend = value => {
		if (value >= 1000000000) {
			return roundToDecimalPlace(value / 1000000000, 0) + 'B';
		} else if (value >= 1000000) {
			return roundToDecimalPlace(value / 1000000, 0) + 'M';
		} else if (value >= 1000) {
			return roundToDecimalPlace(value / 1000, 0) + 'K';
		} else {
			return roundToDecimalPlace(value, 0);
		}
	};

	const chartHeight = 80 + data.categories.length * 45;
	const options = {
		chart: {
			type: 'bar',
			style: { fontFamily: 'Roboto', padding: '10px' },
			marginTop: 10,
			paddingTop: 25,
			spacingBottom: 40,
			height: chartHeight,
			marginRight: 190,
			animation: { duration: 200 },
		},
		title: {
			text: '',
		},
		subtitle: {
			text: '',
			style: {
				display: 'none',
			},
		},
		credits: false,

		xAxis: {
			categories: data.categories,
			labels: {
				style: {
					fontSize: '16px',
					fontFamily: 'Roboto',
				},
			},
		},
		yAxis: {
			min: 0,
			title: {
				text: '',
			},
			labels: false,
			style: {
				color: 'white',
			},
			gridLineWidth: 0,
			minorGridLineWidth: 0,
		},

		navigation: {
			buttonOptions: {
				enabled: false,
			},
		},

		plotOptions: {
			series: {
				stacking: 'percent',
				pointWidth: 35,
				pointPadding: 7,
				trackByArea: true,
				cursor: 'pointer',
				dataLabels: [
					{
						enabled: true,
						style: {
							color: 'black',
							textOutline: 'none',
							fontSize: '14px',
							backgroundColor: 'white',
							padding: 2,
						},
						formatter: function() {
							return this.y === 0 ? '' : roundToDecimalPlace(this.y, 2) + '%';
						},
					},
				],
				states: {
					inactive: {
						opacity: 1,
					},
				},

				events: {
					legendItemClick: function() {
						const chart = this.chart;
						const legend = chart.series;
						const currentLegendId = this.options.id;
						const otherLegend = legend.find(
							s =>
								(s.options.id === 'dualMultiSourced' ||
									s.options.id === 'singleSourced') &&
								s.options.id !== currentLegendId
						);
						if (
							currentLegendId === 'dualMultiSourced' ||
							currentLegendId === 'singleSourced'
						) {
							this.setVisible(!this.visible);

							if (otherLegend && !this.visible) {
								otherLegend.setVisible(true);
							}
							return false;
						}

						return true;
					},

					mouseOver: function() {
						this.chart.tooltip.refresh(this.data[this.data.length - 1]);
					},
					mouseOut: function() {
						this.chart.tooltip.hide();
					},
				},
			},
		},

		exporting: {
			enabled: false,
		},

		tooltip: {
			shared: false,
			stickyTracking: false,
			align: 'top',
			followPointer: true,

			formatter: function() {
				const category = this.x;
				let tooltipText = `<b>${category}</b><br/>`;

				const spendValue =
					this.point.options.spend || this.point.options.totalSpend || 0;
				tooltipText += `Total Spend: <b>${formatSpend(spendValue)}</b><br/>`;

				const singleSpend =
					this.point.options.singleSourceSpend ||
					data.singleSourceSpend[this.point.x];
				const multiSpend =
					this.point.options.mutltisourceSpend ||
					data.mutltisourceSpend[this.point.x];

				if (singleSpend) {
					tooltipText += `Single Sourced Spend: <b>${formatSpend(
						singleSpend
					)}</b><br/>`;
				}
				if (multiSpend) {
					tooltipText += `Dual / Multi Sourced Spend: <b>${formatSpend(
						multiSpend
					)}</b><br/>`;
				}

				return tooltipText;
			},
		},
		series: [
			{
				id: 'dualMultiSourced',
				name: 'Dual / Multi Sourced',
				data: data.mutltisourcePercentage.map((percentage, index) => ({
					y: percentage,
					spend: data.spend[index],
					totalSpend: data.spend[index],
					mutltisourceSpend: data.mutltisourceSpend[index],
				})),
				color: '#77b0be',
			},
			{
				id: 'singleSourced',
				name: 'Single Sourced',
				data: data.singleSourcePrecentage.map((percentage, index) => ({
					y: percentage,
					spend: data.spend[index],
					totalSpend: data.spend[index],
					singleSourceSpend: data.singleSourceSpend[index],
				})),
				color: {
					pattern: {
						path: {
							d: 'M 30 30 L 0 0 L 30 30 L 0 0 z',
							strokeWidth: 1,
						},
						width: 5,
						height: 5,
						color: '#1989A4',
					},
				},
			},
			{
				type: 'column',
				data: data.spend.map((spend, index) => ({
					y: 100,
					x: index,
					spend: spend,
					totalSpend: spend,
					singleSourceSpend: data.singleSourceSpend[index], // Add spend details for tooltip
					mutltisourceSpend: data.mutltisourceSpend[index],
				})),
				color: 'transparent',
				xAxis: 0,
				yAxis: 0,
				showInLegend: false,
				enableMouseTracking: true,
				events: {
					mouseOver: function() {
						this.chart.tooltip.refresh(this.points[0]);
					},
					mouseOut: function() {
						this.chart.tooltip.hide();
					},
				},
				groupPadding: 0,
				pointPadding: 0,
				zIndex: 2,
				dataLabels: [
					{
						enabled: true,
						align: 'right',
						inside: false,
						x: 120,
						crop: false,
						overflow: 'allow',
						formatter: function() {
							const spendValue = this.point.options.spend;
							return '$' + formatSpend(spendValue);
						},
						style: {
							color: 'black',
							fontSize: '15px',
							fontWeight: 'bold',
							textOutline: 'none',
						},
					},
				],
			},
		],
	};
	return (
		<DualSourcedSkuProfileChart
			options={options}
			data={data}
			isLoading={isLoading}
		/>
	);
};
